import { Component } from '@theme/component';

/**
 * Custom announcement bar with mobile dot pagination.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
class CustomAnnouncementBar extends Component {
  #current = 0;
  #interval;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', () => this.#clearTimer());
    this.addEventListener('mouseleave', () => this.#startTimer());
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    this.#startTimer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimer();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  select(index) {
    this.current = parseInt(index, 10);
  }

  next() { this.current += 1; }
  previous() { this.current -= 1; }

  #startTimer() {
    const ms = this.#intervalMs;
    if (!ms) return;
    this.#clearTimer();
    this.#interval = setInterval(() => {
      if (!document.hidden) this.next();
    }, ms);
  }

  #clearTimer() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  get #intervalMs() {
    const val = parseInt(this.getAttribute('autoplay'), 10);
    return Number.isNaN(val) ? undefined : val * 1000;
  }

  #onVisibilityChange = () => document.hidden ? this.#clearTimer() : this.#startTimer();

  get current() { return this.#current; }

  set current(value) {
    this.#current = value;
    const slides = this.refs.slides ?? [];
    const dots = this.refs.dots ?? [];
    const len = slides.length;
    if (!len) return;

    const rel = ((value % len) + len) % len;

    for (const [i, slide] of slides.entries()) {
      slide.setAttribute('aria-hidden', String(i !== rel));
    }
    for (const [i, dot] of dots.entries()) {
      dot.setAttribute('aria-selected', String(i === rel));
    }
  }
}

if (!customElements.get('custom-announcement-bar-component')) {
  customElements.define('custom-announcement-bar-component', CustomAnnouncementBar);
}
